import { computed, ref } from 'vue';
import { isEventType } from '@/infrastructure/views/networkDeviceView/networkDeviceDetail/networkDeviceAlert/networkDeviceAlert.setup';
import { i18n, useInternational } from '@/common/locale';
import { columnFormatter, resolveAlertStatUnit, useAlertFormatter } from '@/alert/utils/utils';
import {
  tagsAndTargetsController,
  TARGET_TYPE,
  useAlertTagsAndTargets,
} from '@/alert/utils/tagsAndTargets.uses';
import { getRuleNameIcon } from '@/alert/components/alertGrid/alertGrid.utils';
import { ExtractedCustomColumnTreeData } from '@/common/utils';
import {
  SEARCHABLE_ALERT_COLUMNS,
  VIEW_ONLY_ALERT_COLUMNS,
  SEARCHABLE_ALERT_COLUMNS_HAS_NOTI,
  SEARCHABLE_ALERT_COLUMNS_HAS_EXTERNAL_LABEL,
} from '@/alert/components/alertGrid/alertGrid.define';
import {
  CHECK_BY_COMBINATION,
  CHECK_BY_TARGET,
  getDuringTimeItems,
  getEvaluateValueItems,
} from '@/alert/utils/define';
import { lowerCase } from 'lodash-es';

export {
  filterCustomAlertChildren,
  isCustomAlertRule,
} from '@/alert/utils/alertMetricSubType.utils';
import { useMessage } from '@/common/inject/useMessage';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';
import { useGetV7NdmDeviceMonitoringAlertMessageByDeviceIdAndAlertRuleId } from '@/api-client/ndm/queries/ndm-monitoring-controller/ndm-monitoring-controller';
import { toNullableNumber } from '@/common/utils/core/number/parse';

interface EventAlertInfo {
  messageText: string;
  networkInfo: Record<string, string>;
}

export const useEventTypeValue = () => {
  const { t } = useInternational();
  const { showErrorMsg } = useMessage();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const isShowMessageWindow = ref(false);
  const eventAlertInfo = ref<EventAlertInfo>({
    messageText: '',
    networkInfo: {},
  });

  // Vue Query 파라미터
  const deviceId = ref<string>('');
  const alertRuleId = ref<string>('');
  const currentEventType = ref<string>('');
  const frameName = ref<string>('');

  const queryParams = computed(() => ({
    eventType: currentEventType.value,
  }));

  const setEventInfo = (eventType: string, data: any) => {
    if (!data) return;

    if (eventType.toLowerCase() === 'syslog') {
      const { facility, severity, message } = data.sysLogMessage;
      eventAlertInfo.value = {
        messageText: message,
        networkInfo: { facility, severity },
      };
    } else if (eventType.toLowerCase() === 'trap') {
      const { genericTrapType, specificTrapCode, enterpriseOid, message } = data.snmpTrapMessage;
      eventAlertInfo.value = {
        messageText: message,
        networkInfo: { genericTrapType, specificTrapCode, enterpriseOid },
      };
    }
  };

  // Vue Query 훅
  const {
    data: queryData,
    isPending,
    refetch,
  } = useGetV7NdmDeviceMonitoringAlertMessageByDeviceIdAndAlertRuleId(
    deviceId,
    alertRuleId,
    queryParams,
    {
      request: {
        frameName: frameName.value,
      },
      query: {
        enabled: computed(() => false),
        meta: {
          onError: (error: unknown) => {
            errorHandlerWithMessage({ error });
          },
        },
        select: (response) => response?.data?.data?.[0],
      },
    },
  );

  const onClickValueCell = async ({ value, row, ruleId, targetFrameName }) => {
    if (!isEventType(value) || isPending) {
      return;
    }

    const eventType = row.value || value;
    const targetDeviceId = row.targets[0]?.id;
    const targetAlertRuleId = row.ruleId || ruleId;

    if (!targetDeviceId) {
      showErrorMsg({
        msgStr: t('MESSAGE.NO_DATA'),
      });
      return;
    }

    isShowMessageWindow.value = true;

    currentEventType.value = eventType;
    deviceId.value = targetDeviceId;
    alertRuleId.value = targetAlertRuleId;
    frameName.value = targetFrameName;

    try {
      await refetch();
      setEventInfo(currentEventType.value, queryData.value);
    } catch (error: unknown) {
      errorHandlerWithMessage({ error });
    }
  };

  return {
    isShowMessageWindow,
    eventAlertInfo,
    onClickValueCell,
  };
};

export type AlertTreeRow = ExtractedCustomColumnTreeData<typeof VIEW_ONLY_ALERT_COLUMNS>;
export type AlertRowFormatter<Row = any> = Record<string, (val: any, row: Row) => any>;
export type AlertThresholdItem = {
  alertLevel: 'normal' | 'warning' | 'critical';
  evaluateValue: string;
  duringTimeInterval: string;
  comparison: string;
  warning: string;
  critical: string;
  criticalComparison: string;
  warningComparison: string;
  alertName?: string;
  conditionType?: string;
  formulaAlias?: string;
};

export type AlertThresholdInfo = {
  alertLevel: 'normal' | 'warning' | 'critical';
  evaluateValue: string;
  duringTimeInterval: string;
  comparison: string;
  warning: number | null;
  critical: number | null;
  alertName?: string;
  conditionType?: string;
  formulaAlias?: string;
};

// Locale별 셀렉트박스 라벨을 캐싱해 반복 조회를 줄이면서도 locale 변경에 반응하도록 유지
const evaluateValueMapCache: Record<string, Map<string, string>> = {};
const duringTimeMapCache: Record<string, Map<string, string>> = {};

const getLocaleKey = () => {
  const { locale } = i18n.global;
  return typeof locale === 'string' ? locale : locale.value;
};

const getEvaluateValueMap = () => {
  const localeKey = getLocaleKey();
  if (!evaluateValueMapCache[localeKey]) {
    const items = getEvaluateValueItems();
    const map = new Map(items.map((v) => [v.value, v.name]));

    // 레거시 max/min 값도 current 라벨로 표시되도록 Map 생성 시 함께 등록한다
    const currentLabel = map.get('current') ?? i18n.global.t('WORD.CURRENT');
    ['max', 'min'].forEach((legacyKey) => {
      if (!map.has(legacyKey)) {
        map.set(legacyKey, currentLabel);
      }
    });

    evaluateValueMapCache[localeKey] = map;
  }
  return evaluateValueMapCache[localeKey];
};

const getDuringTimeMap = () => {
  const localeKey = getLocaleKey();
  if (!duringTimeMapCache[localeKey]) {
    const items = getDuringTimeItems();
    duringTimeMapCache[localeKey] = new Map(items.map((v) => [v.value, v.name]));
  }
  return duringTimeMapCache[localeKey];
};

const getThreshold = ({
  alertLevel,
  evaluateValue,
  duringTimeInterval,
  critical,
  criticalComparison,
  warning,
  warningComparison,
  alertName,
  conditionType,
  formulaAlias,
}: AlertThresholdItem): AlertThresholdInfo => {
  const evalMap = getEvaluateValueMap();
  const durationMap = getDuringTimeMap();

  const evaluateValueKey = evaluateValue ?? '';
  const duringTimeKey = duringTimeInterval ?? '';

  // Map 조회(O(1))로 전환하면서 기존 빈 문자열 처리 방식 유지
  const evaluateValueName = evaluateValueKey ? (evalMap.get(evaluateValueKey) ?? '') : '';
  const duringTimeName = duringTimeKey ? (durationMap.get(duringTimeKey) ?? '') : '';

  return {
    alertLevel,
    evaluateValue: evaluateValueName,
    duringTimeInterval: duringTimeName,
    comparison: warningComparison || criticalComparison,
    warning: toNullableNumber(warning),
    critical: toNullableNumber(critical),
    alertName,
    conditionType,
    formulaAlias,
  };
};

export const useAlertTreeGrid = ({
  columnOption,
  showNotiStatus = false,
  showExternalLabel = false,
}: {
  columnOption: 'viewOnly' | 'searchable';
  showNotiStatus?: boolean;
  showExternalLabel?: boolean;
}) => {
  let alertColumns;
  // TODO: 안전하게 switch나 함수 조건문으로 변경 필요
  if (columnOption === 'viewOnly') {
    alertColumns = VIEW_ONLY_ALERT_COLUMNS;
  } else if (showExternalLabel) {
    alertColumns = SEARCHABLE_ALERT_COLUMNS_HAS_EXTERNAL_LABEL;
  } else if (showNotiStatus) {
    // TODO: 추후 개선선
    alertColumns = SEARCHABLE_ALERT_COLUMNS_HAS_NOTI;
  } else {
    alertColumns = SEARCHABLE_ALERT_COLUMNS;
  }

  const { makeAlertTreeRows: makeRows } = useAlertTagsAndTargets();
  const alertFormatter = useAlertFormatter();

  const makeAlertTreeRows = <Row extends { ruleCriteria?: string }>({
    data,
    formatter = {},
  }: {
    data: Row[];
    formatter?: AlertRowFormatter;
  }) =>
    makeRows({
      data,
      columns: alertColumns,
      targetType: TARGET_TYPE.NONE,
      formatter: {
        ruleNameIcon: (_, { ruleCriteria }) => ({
          iconClass: getRuleNameIcon({ ruleCriteria: ruleCriteria || CHECK_BY_TARGET }),
          tooltip: ruleCriteria || CHECK_BY_TARGET,
        }),
        alertName: (_, { alertName, alertRuleType, parent, threshold }) => {
          const type = lowerCase(alertRuleType || parent?.alertRuleType);
          if (type === 'metric' || type === 'table') {
            const alertNameValue = alertName || threshold?.alertName;
            return threshold?.formulaAlias || alertFormatter.alertName(alertNameValue);
          }
          return alertName;
        },
        targets: (_, { ruleCriteria, targets }) => {
          // by combination 부모
          if (ruleCriteria === CHECK_BY_COMBINATION) {
            return tagsAndTargetsController[TARGET_TYPE.TARGETS]([]).targets;
          }

          return tagsAndTargetsController[TARGET_TYPE.TARGETS](targets).targets;
        },
        targetAlias: (_, { ruleCriteria, targets }) => {
          if (ruleCriteria === CHECK_BY_COMBINATION || !Array.isArray(targets)) return null;

          const result = targets.reduce((acc, t) => {
            if (t.targetAlias) {
              acc.push({ name: t.targetAlias });
            }
            return acc;
          }, []);

          return result.length ? result : null;
        },
        threshold: (_, row) => {
          const { ruleCriteria, alertRuleType, alertLevel, threshold } = row;
          // system 또는 by combination 부모
          if (
            ['system'].includes(lowerCase(alertRuleType)) ||
            ruleCriteria === CHECK_BY_COMBINATION
          ) {
            return null;
          }

          const thresholdData = Array.isArray(threshold) ? threshold[0] : threshold || {};
          // by target
          if (ruleCriteria === CHECK_BY_TARGET) {
            return getThreshold({ ...thresholdData, alertLevel: lowerCase(alertLevel) });
          }

          // by combination 자식
          return getThreshold({
            ...thresholdData,
            alertLevel: lowerCase(alertLevel),
          });
        },
        value: (_, { value }) => columnFormatter.value(value),
        statUnit: (_, row) => resolveAlertStatUnit(row, alertFormatter),
        ...formatter,
      },
    });

  return {
    alertColumns,
    makeAlertTreeRows,
  };
};
